import { Database, Q } from '@nozbe/watermelondb';

import InvoiceInvoicingProduct from '../../../models/InvoiceInvoicingProduct';
import {
    OrganisationModel,
    OrganisationPayload,
    PreparedDataOnOrganisationUpdate,
    OrganisationEntityTableName,
    ModelByOrganisationEntityTable,
} from '../../../../types/Organisation';
import { InvoiceDBServiceOptions } from '../../../../types/dbService';
import { InvoiceModel } from '../../../../types/Invoice';
import { UserModel, UserPayload } from '../../../../types/User';
import { ValidationOperation } from '../../../validation/validator';
import Invoice from '../../Invoice';
import { calculateInvoiceDetailsFromItems } from '../../../../utils/invoicing';
import { getProceduresFromEntries } from '../../../../utils/procedures';
import { addRelatedDataBatch } from '../entry/common';

const toPreparedDataItems = <K extends OrganisationEntityTableName>(
    records: ModelByOrganisationEntityTable[K][],
    table: K,
    operation: ValidationOperation,
) =>
    records.map((record) => ({
        record,
        table,
        operation,
    }));

type OrganisationUpdatePayload = OrganisationPayload & {
    taxRate?: string;
    productsTaxRate?: string;
};

const shouldUpdateInvoicesTaxRates = ({
    payload,
}: {
    payload: OrganisationUpdatePayload;
}) => {
    const {
        accountingProvider,
        updateInvoicesTaxRate,
        updateInvoicesProductsTaxRate,
    } = payload;

    return (
        (updateInvoicesTaxRate || updateInvoicesProductsTaxRate) &&
        accountingProvider === 'internal'
    );
};

const prepareUpdateOrganisation = ({
    orgElement,
    payload,
}: {
    orgElement: OrganisationModel;
    payload: OrganisationUpdatePayload;
}) => {
    const {
        address,
        alternativeAddress,
        paymentInstructons,
        alternativeEmail,
        alternativePhone,
        alternativePhonePrefix,
        footerMessage,
        taxRate,
        productsTaxRate,
        businessNumber,
        isAlternativePhoneEnabled,
        isAlternativeEmailEnabled,
        defaultInvoiceDueDays,
    } = payload;

    return orgElement.prepareUpdate((organisation) => {
        organisation.name = payload.name || orgElement.name;
        organisation.address = address || orgElement.address;
        organisation.alternativeAddress =
            alternativeAddress || orgElement.alternativeAddress;
        organisation.paymentInstructions =
            paymentInstructons || orgElement.paymentInstructions;
        organisation.alternativeEmail =
            alternativeEmail || orgElement.alternativeEmail;
        organisation.alternativePhoneNumber =
            alternativePhone || orgElement.alternativePhoneNumber;
        organisation.footerMessage = footerMessage;

        if ('taxRate' in payload) {
            organisation.taxRate = taxRate;
        }
        if ('productsTaxRate' in payload) {
            organisation.productsTaxRate = productsTaxRate;
        }
        if ('defaultInvoiceDueDays' in payload) {
            organisation.defaultInvoiceDueDays = defaultInvoiceDueDays;
        }

        organisation.businessNumber =
            businessNumber || orgElement.businessNumber;
        organisation.alternativePhonePrefix =
            alternativePhonePrefix || orgElement.alternativePhonePrefix;
        organisation.isAlternativePhoneEnabled = isAlternativePhoneEnabled;
        organisation.isAlternativeEmailEnabled = isAlternativeEmailEnabled;
    });
};

type InvoiceUpdateChanges = {
    invoice: InvoiceModel;
    total: string;
    amountCheck: string;
    taxRate?: string;
    productsTaxRate?: string;
};

const collectOrganisationInvoiceUpdateData = async ({
    database,
    invoiceServiceOptions,
    payload,
    taxRate,
    productsTaxRate,
}: {
    database: Database;
    invoiceServiceOptions: InvoiceDBServiceOptions;
    payload: OrganisationUpdatePayload;
    taxRate?: string;
    productsTaxRate?: string;
}) => {
    const {
        accountingProvider,
        updateInvoicesTaxRate,
        updateInvoicesProductsTaxRate,
    } = payload;

    const invoiceService = new Invoice(invoiceServiceOptions);
    const taxedDraftInvoices =
        await invoiceService.getProviderTaxedDraftInvoices(
            accountingProvider as 'internal',
        );

    const invoiceUpdates: InvoiceUpdateChanges[] = [];

    for (const draftInvoice of taxedDraftInvoices) {
        if (!draftInvoice.applyTaxes) {
            continue;
        }

        const products = await database.collections
            .get<InvoiceInvoicingProduct>('invoice_invoicing_products')
            .query(Q.where('invoice_id', draftInvoice.id))
            .fetch();

        const entries = await draftInvoice.entries.fetch();

        const shouldAssignDefaultTaxForProducts =
            products.length > 0 &&
            updateInvoicesTaxRate &&
            (!entries.length ||
                draftInvoice.taxRate === draftInvoice.productsTaxRate);

        const taxRateToCalculate = updateInvoicesTaxRate
            ? taxRate
            : draftInvoice.taxRate;

        const productsTaxRateToCalculate = updateInvoicesProductsTaxRate
            ? productsTaxRate
            : shouldAssignDefaultTaxForProducts
            ? taxRate
            : draftInvoice.productsTaxRate;

        const procedures = await getProceduresFromEntries(entries);

        const { totalSum, subtotalSum } = calculateInvoiceDetailsFromItems({
            procedures,
            products,
            taxRate: Number(taxRateToCalculate) || 0,
            productsTaxRate: Number(productsTaxRateToCalculate) || 0,
        });

        const changes: InvoiceUpdateChanges = {
            invoice: draftInvoice,
            total: totalSum.toString(),
            amountCheck: subtotalSum.toString(),
        };

        if (updateInvoicesTaxRate && entries.length > 0) {
            changes.taxRate = taxRate;
        }

        if (updateInvoicesProductsTaxRate && products.length > 0) {
            changes.productsTaxRate = productsTaxRate;
        } else if (shouldAssignDefaultTaxForProducts) {
            changes.productsTaxRate = taxRate;
        }

        invoiceUpdates.push(changes);
    }

    return invoiceUpdates;
};

const prepareOrganisationInvoiceUpdates = (
    invoiceUpdates: InvoiceUpdateChanges[],
) =>
    invoiceUpdates.map(
        ({ invoice, total, amountCheck, taxRate, productsTaxRate }) =>
            invoice.prepareUpdate((record) => {
                if (taxRate != null) {
                    record.taxRate = taxRate;
                }

                if (productsTaxRate != null) {
                    record.productsTaxRate = productsTaxRate;
                }

                record.total = total;
                record.amountCheck = amountCheck;
            }),
    );

const prepareUpdateUserFromPostPayload = ({
    user,
    userPayload,
}: {
    user: UserModel;
    userPayload: UserPayload;
}) =>
    user.prepareUpdate((userElement) => {
        for (const key in userPayload) {
            userElement[key] = userPayload[key];
        }
    });

const appendPreparedOrganisation = (
    preparedData: PreparedDataOnOrganisationUpdate[],
    organisation: OrganisationModel,
) => [
    ...preparedData,
    ...toPreparedDataItems([organisation], 'organisations', 'update'),
];

const appendPreparedInvoices = (
    preparedData: PreparedDataOnOrganisationUpdate[],
    invoices: InvoiceModel[],
) => [...preparedData, ...toPreparedDataItems(invoices, 'invoices', 'update')];

const appendPreparedUser = (
    preparedData: PreparedDataOnOrganisationUpdate[],
    user: UserModel,
) => [...preparedData, ...toPreparedDataItems([user], 'users', 'update')];

const logUpdateDBActions = ({
    orgElement,
    logDBAction,
    tableName,
    userUpdated,
}: {
    orgElement: OrganisationModel;
    logDBAction: (params: {
        message: string;
        modelName: string;
        payload: unknown;
    }) => void;
    tableName: string;
    userUpdated?: UserModel;
}) => {
    logDBAction({
        message: 'Update organisation',
        modelName: tableName,
        payload: orgElement,
    });

    if (userUpdated) {
        logDBAction({
            message: 'Update user',
            modelName: 'users',
            payload: userUpdated,
        });
    }
};

export {
    addRelatedDataBatch,
    appendPreparedInvoices,
    appendPreparedOrganisation,
    appendPreparedUser,
    collectOrganisationInvoiceUpdateData,
    logUpdateDBActions,
    prepareOrganisationInvoiceUpdates,
    prepareUpdateOrganisation,
    prepareUpdateUserFromPostPayload,
    shouldUpdateInvoicesTaxRates,
};
